// Canonical auto-title generation for menu bundles / deliverables / day-menu
// slots. This is the SINGLE source of truth — client and server both import it
// so a bundle titles identically everywhere.
//
// A title lists component display names (falling back to the component name) in
// canonical component-kind order, joined by ' • '. Multiple components sharing a
// kind are grouped together, ordered alphabetically, and joined by ' & ', e.g.
// two mains render as "Chicken & Rice". Beverages (incl. milk) always sort last.
//
// Titles NEVER carry quantity counts: an item attached with quantity > 1, or
// legacy duplicate rows for the same item, render ONCE as the plain name
// ("Orange Cups", never "2x Orange Cups"). Quantity is a deliverable-level
// concern and does not surface in auto titles. Identity for the duplicate
// collapse is (kind, case-insensitive label).
//
// Condiments are NOT part of the title: they render as a separate description
// line ("Condiments: X, Y, Z") built by buildBundleCondimentDescription and
// stored/displayed as subtext. A components list that is ONLY condiments keeps
// them in the title rather than rendering the empty label.

import { MENU_TITLE_SEPARATOR } from './menu-item-title'

// Canonical ordering of component kinds within a title. Kinds not listed here
// (or null) sort after these, preserving their original order.
export const BUNDLE_TITLE_KIND_ORDER: readonly string[] = [
  'MAIN_ENTREE',
  'GRAIN',
  'SIDE',
  'VEGETABLE',
  'FRUIT',
  'CONDIMENT',
  'SUPPLEMENT',
  'BEVERAGE',
]

export interface BundleTitleComponent {
  displayName?: string | null
  name?: string | null
  kind?: string | null
}

export interface BuildBundleAutoTitleOptions {
  // When true, appends a "Milk" beverage unless a milk component is already present.
  milkIncluded?: boolean
  // Returned when no components resolve to a label.
  emptyLabel?: string
}

// Beverages (incl. milk) always sort last. Many milk/beverage entries come
// through as bundle/"milk group" items with no component kind, so also treat a
// milk-labelled entry as a beverage even when its kind is unknown.
function isMilkLabel(label: string): boolean {
  return /\bmilk\b/i.test(label)
}

function isBeverageLike(kind: string | null | undefined, label: string): boolean {
  return kind === 'BEVERAGE' || (!kind && isMilkLabel(label))
}

/**
 * The canonical sort rank of one title entry — EXPORTED so surfaces that
 * order companion data alongside a title (e.g. per-component serving sizes
 * under a deliverable row) sort by the exact same rule instead of
 * re-deriving it. Lower ranks first; beverages/milk absolute last.
 */
export function bundleTitleKindRank(kind: string | null | undefined, label: string): number {
  return kindRank(kind, label)
}

function kindRank(kind: string | null | undefined, label: string): number {
  // Absolute last, ahead of any known food kind or unknown/bundle item.
  if (isBeverageLike(kind, label)) {
    return Number.MAX_SAFE_INTEGER
  }
  if (!kind) {
    // Unknown/null (e.g. bundles) sort after known food kinds but before beverages.
    return BUNDLE_TITLE_KIND_ORDER.length
  }
  const rank = BUNDLE_TITLE_KIND_ORDER.indexOf(kind)
  return rank === -1 ? BUNDLE_TITLE_KIND_ORDER.length : rank
}

// Resolve a component's label: display name if present, otherwise the raw name.
function componentLabel(component: BundleTitleComponent): string {
  return (component.displayName ?? component.name ?? '').trim()
}

// Matches a label carrying a legacy quantity prefix ("2x Orange Cups") from
// data written while titles/descriptions still rendered counts.
const COUNTED_LABEL_PATTERN = /^(\d+)x\s+(.+)$/

/**
 * Dedupe labels for plain (count-free) display. Legacy "Nx " quantity prefixes
 * are stripped, so "Ketchup" + "2x Ketchup" collapses to "Ketchup". Matching is
 * case-insensitive; the first spelling and first-seen order win. Blank labels
 * are dropped.
 */
export function dedupePlainLabels(labels: string[]): string[] {
  const result: string[] = []
  const seen = new Set<string>()
  for (const raw of labels) {
    const trimmed = raw.trim()
    if (!trimmed) {
      continue
    }
    const counted = COUNTED_LABEL_PATTERN.exec(trimmed)
    const label = counted ? counted[2] : trimmed
    const key = label.toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      result.push(label)
    }
  }
  return result
}

// A materialized meal Bundle is unique on (mealtime, name). When two distinct
// meals auto-title identically (e.g. condiments are excluded from the title, so
// two meals differing only in condiments collide) the materializer keeps names
// distinct by suffixing the raw `name` with " (Meal)" / " (Meal N)". That suffix
// is a storage-uniqueness artifact and must NEVER reach a user — the clean title
// lives on Bundle.displayName. This strips a trailing disambiguation suffix as a
// belt-and-suspenders fallback wherever a bundle's raw `name` still surfaces
// (e.g. legacy rows written before displayName was populated).
const MEAL_DISAMBIGUATION_SUFFIX = /\s*\(Meal(?:\s+\d+)?\)$/

export function stripBundleMealSuffix(title: string | null | undefined): string {
  return (title ?? '').replace(MEAL_DISAMBIGUATION_SUFFIX, '')
}

/**
 * ALL member labels of a bundle/meal (condiments INCLUDED) in the same
 * canonical display order the auto-title uses — kind rank, then alphabetical
 * label (input order breaks exact-label ties).
 * Legacy "Nx " count prefixes are stripped and duplicates collapse on
 * (kind, case-insensitive label), mirroring the title's collapse rule. This is
 * the "Includes: …" companion of `buildBundleAutoTitle`: the title omits
 * condiments, so surfaces that need the FULL composition (e.g. the pricing
 * matrix row subtext) list it from here instead of re-deriving it.
 */
export function orderedBundleMemberLabels(components: BundleTitleComponent[]): string[] {
  const labelled = components
    .map((component) => {
      const raw = componentLabel(component)
      const counted = COUNTED_LABEL_PATTERN.exec(raw)
      return { label: counted ? counted[2]! : raw, kind: component.kind ?? null }
    })
    .filter((entry) => entry.label.length > 0)
  const collapsed: Array<{ label: string; kind: string | null }> = []
  const seen = new Set<string>()
  for (const entry of labelled) {
    const key = `${entry.kind ?? ''}::${entry.label.toLowerCase()}`
    if (!seen.has(key)) {
      seen.add(key)
      collapsed.push(entry)
    }
  }
  return collapsed
    .map((entry, index) => ({ ...entry, index }))
    .sort((left, right) => {
      const rankDelta = kindRank(left.kind, left.label) - kindRank(right.kind, right.label)
      if (rankDelta !== 0) {
        return rankDelta
      }
      const labelDelta = compareTitleLabels(left.label, right.label)
      return labelDelta !== 0 ? labelDelta : left.index - right.index
    })
    .map((entry) => entry.label)
}

// Case-insensitive alphabetical label comparison for within-kind ordering —
// locale-stable (plain codepoint order on the lowercased labels) so titles
// never differ across runtimes/ICU versions; they are persisted identity-ish
// strings (Bundle.name collision suffixes hang off them).
function compareTitleLabels(left: string, right: string): number {
  const l = left.toLowerCase()
  const r = right.toLowerCase()
  return l < r ? -1 : l > r ? 1 : 0
}

// A single title/label entry — a resolved label string plus its component kind
// (used for ordering + grouping). Both public builders resolve their inputs to
// this shape, then share `composeOrderedGroups` for the rest.
interface OrderedTitleEntry {
  label: string
  kind: string | null
}

/**
 * Shared composition tail for the bundle title builders: condiment exclusion,
 * optional milk injection, empty fallback, dedupe on (kind, case-insensitive
 * label), kind-rank sort (beverages/milk last, alphabetical within a kind),
 * then ' & ' within a kind
 * and ' • ' between kinds. The dedupe key uses the FULL label, so two entries
 * whose labels differ only by a serving-size suffix stay distinct.
 */
function composeOrderedGroups(entries: OrderedTitleEntry[], options: BuildBundleAutoTitleOptions): string {
  // Condiments live in the description line, not the title — unless they are
  // all there is.
  const nonCondiments = entries.filter((entry) => entry.kind !== 'CONDIMENT')
  const kept = nonCondiments.length > 0 ? nonCondiments : entries.slice()

  if (options.milkIncluded && !kept.some((entry) => entry.label.toLowerCase().includes('milk'))) {
    kept.push({ label: 'Milk', kind: 'BEVERAGE' })
  }

  if (kept.length === 0) {
    return options.emptyLabel ?? '--'
  }

  // Collapse the same item passed multiple times (legacy duplicate rows) into
  // one plain entry — no count prefix. Identity = (kind, case-insensitive
  // label); duplicates keep the first occurrence's position and spelling.
  const collapsed: OrderedTitleEntry[] = []
  const collapsedKeys = new Set<string>()
  for (const entry of kept) {
    const key = `${entry.kind ?? ''}::${entry.label.toLowerCase()}`
    if (!collapsedKeys.has(key)) {
      collapsedKeys.add(key)
      collapsed.push({ ...entry })
    }
  }

  // Sort by canonical kind order; entries of the SAME rank order
  // alphabetically by label (input order breaks exact-label ties). Feed order
  // is arbitrary to a reader — the materializer emits picks in database-id
  // order, the template editor in authoring order — so without the label sort
  // the same two components could title "Cereal & String Cheese" on one day
  // and "String Cheese & Cereal" on another.
  const ordered = collapsed
    .map((entry, index) => ({ ...entry, index }))
    .sort((left, right) => {
      const rankDelta = kindRank(left.kind, left.label) - kindRank(right.kind, right.label)
      if (rankDelta !== 0) {
        return rankDelta
      }
      const labelDelta = compareTitleLabels(left.label, right.label)
      return labelDelta !== 0 ? labelDelta : left.index - right.index
    })

  // Group consecutive same-kind entries. Only non-null kinds group together;
  // null-kind entries (e.g. bundles) each stand alone.
  const groups: string[] = []
  let groupKind: string | null = null
  let groupLabels: string[] = []
  const flush = () => {
    if (groupLabels.length > 0) {
      groups.push(groupLabels.join(' & '))
    }
  }
  for (const entry of ordered) {
    if (groupLabels.length > 0 && entry.kind != null && entry.kind === groupKind) {
      groupLabels.push(entry.label)
    } else {
      flush()
      groupKind = entry.kind
      groupLabels = [entry.label]
    }
  }
  flush()

  return groups.join(MENU_TITLE_SEPARATOR)
}

export function buildBundleAutoTitle(
  components: BundleTitleComponent[],
  options: BuildBundleAutoTitleOptions = {},
): string {
  const entries = components
    .map((component) => ({ label: componentLabel(component), kind: component.kind ?? null }))
    .filter((entry) => entry.label.length > 0)
  return composeOrderedGroups(entries, options)
}

export interface BundleCatalogTitleMember {
  // Component CATALOG name (never the family displayName).
  name?: string | null
  kind?: string | null
  // The serving size's saved label (RecipeServingSize.name), appended as
  // " (label)" — e.g. "Beef Patty (4 oz)". Omitted when absent.
  servingSizeLabel?: string | null
}

/**
 * The granular "technical" composition of a bundle: each member's CATALOG name
 * with its serving size appended, in the same canonical order/grouping as
 * `buildBundleAutoTitle`. Where the display title collapses portion/product
 * variants of one meal to an identical string, this keeps them distinct — the
 * dedupe identity includes the serving-size suffix, so two picks of the same
 * catalog name at different serving sizes both appear. Condiments are excluded
 * (same rule as the title). Surfaced as catalog subtext, never to families.
 */
export function buildBundleCatalogTitle(
  members: BundleCatalogTitleMember[],
  options: BuildBundleAutoTitleOptions = {},
): string {
  const entries = members
    .map((member) => {
      const base = (member.name ?? '').trim()
      const size = member.servingSizeLabel?.trim()
      return { label: base && size ? `${base} (${size})` : base, kind: member.kind ?? null }
    })
    .filter((entry) => entry.label.length > 0)
  return composeOrderedGroups(entries, options)
}

// The companion description line for the condiments excluded from the title,
// e.g. "Condiments: Ketchup, Ranch". Returns null when no condiments resolve
// to a label. A condiment attached more than once (or carried with a legacy
// quantity) lists once with its plain name — no count. Stored on
// Bundle.description and rendered as non-bold subtext under auto titles.
export function buildBundleCondimentDescription(components: BundleTitleComponent[]): string | null {
  const labels = dedupePlainLabels(
    components.filter((component) => component.kind === 'CONDIMENT').map((component) => componentLabel(component)),
  )
  if (labels.length === 0) {
    return null
  }
  return `Condiments: ${labels.join(', ')}`
}

// Inverse of buildBundleCondimentDescription — recognizes the auto-generated
// "Condiments: X, Y, Z" line (and nothing else; hand-written descriptions
// don't parse).
const CONDIMENT_DESCRIPTION_PATTERN = /^Condiments:\s*(.+)$/

/**
 * Parse a stored `Bundle.description` back into the condiment labels it was
 * generated from. Returns `[]` for null/blank/non-matching descriptions.
 */
export function parseBundleCondimentDescription(description: string | null | undefined): string[] {
  const match = CONDIMENT_DESCRIPTION_PATTERN.exec((description ?? '').trim())
  if (!match) {
    return []
  }
  return dedupePlainLabels(match[1]!.split(','))
}
